import { createSelector } from 'reselect';
import type { Option } from '@branchjs/neo-components';
import { createInitialState } from '../reducers/DruidReducer';
import type { DruidReducer, Filters } from '../../main/types/druidTypes';
import { mapToEventName } from '../support/utils';
import { getFlipperState } from '../../redux/domains/FlipperUpdates/Flipper.selectors';
import type { RootState } from 'client/js/redux';

const EMPTY_ARRAY = [];
const EMPTY_OBJECT = {};

export function getDruidState(state: RootState): DruidReducer {
  return (state && state.druid) || createInitialState();
}

export const getDruidFilters: (state: RootState) => Filters = createSelector(
  getDruidState,
  (druidState) => druidState.filters || EMPTY_OBJECT
);

export const getDruidFeatureFilterOptions = createSelector<RootState, Filters, Option[]>(
  getDruidFilters,
  (druidFilters: Filters) => {
    const { last_attributed_touch_data_tilde_feature } = druidFilters;
    return last_attributed_touch_data_tilde_feature.map((option: string) => ({ label: option, value: option }));
  }
);

export const getDruidChannelFilterOptions = createSelector<RootState, Filters, Option[]>(
  getDruidFilters,
  (druidFilters: Filters) => {
    const { last_attributed_touch_data_tilde_channel } = druidFilters;
    return last_attributed_touch_data_tilde_channel.map((option: string) => ({ label: option, value: option }));
  }
);

export const getDruidCampaignFilterOptions = createSelector<RootState, Filters, Option[]>(
  getDruidFilters,
  (druidFilters: Filters) => {
    const { last_attributed_touch_data_tilde_campaign } = druidFilters;

    return last_attributed_touch_data_tilde_campaign.map((option: string) => ({ label: option, value: option }));
  }
);

export const selectDruidAppVersions = createSelector<RootState, Filters, string[]>(
  getDruidFilters,
  (druidFilters: Filters) => {
    const { user_data_app_version } = druidFilters;

    return user_data_app_version || [];
  }
);

export const getDruidTagsFilterOptions = createSelector<RootState, Filters, Option[]>(
  getDruidFilters,
  (druidFilters: Filters) => {
    const { last_attributed_touch_data_tilde_tags } = druidFilters;

    return last_attributed_touch_data_tilde_tags.map((option: string) => ({ label: option, value: option }));
  }
);

export const getQueries: (state: RootState) => any = createSelector(getDruidState, (state) => ({ ...state.queries }));

export const getReportTotals: (state: RootState) => any = createSelector(getDruidState, (state) => ({
  ...state.reportTotals,
}));

export const getLoadingState: (state: RootState) => boolean = createSelector(getQueries, (queries) =>
  Object.values(queries)
    .map((query: any) => query.loading)
    .some(Boolean)
);

export const getAllEventNamesLast30d: (state: RootState) => string[] = createSelector(getDruidState, (druid) => {
  return (druid.allEventsLast30d || []).filter(Boolean).map(mapToEventName);
});

export const selectAllEventsLast30d: (state: RootState) => string[] = createSelector(getDruidState, (druid) => {
  return druid.allEventsLast30d || [];
});

export const selectAllEventAliasesLast30d: (state: RootState) => Record<string, string[]> = createSelector(
  getDruidState,
  (state) => state.allEventAliasesLast30d || EMPTY_OBJECT
);

function flattenEventAliases(eventAliasesByEventName: Record<string, string[]>): string[] {
  return Object.values(eventAliasesByEventName).reduce((allEventAliases, eventAliases) => {
    return [...allEventAliases, ...eventAliases];
  }, []);
}

export const getDedupedAllEventNamesLast30d: (state: RootState) => string[] = createSelector(
  getAllEventNamesLast30d,
  (allEventNamesLast30d) => {
    const dedupedAllEventNames = allEventNamesLast30d.filter(
      (eventName, index, allEventNames) => allEventNames.indexOf(eventName) === index
    );
    return dedupedAllEventNames.map(mapToEventName);
  }
);

export const selectFlattenedEventAliasesLast30d: (state: RootState) => string[] = createSelector(
  selectAllEventAliasesLast30d,
  (allEventAliasesLast30d) => {
    return flattenEventAliases(allEventAliasesLast30d);
  }
);

export const getAllEventAliasesForEvent: (
  state: RootState,
  props?: {
    eventName?: string;
  }
) => string[] = createSelector(
  getDruidState,
  (state, props) => (props && props.eventName) || '',
  (state, eventName) => (state.allEventAliasesLast30d || {})[eventName] || EMPTY_ARRAY
);

/**
 * Get list of custom events from druid state.
 */
export const getCustomEventList: (state: RootState) => string[] = createSelector(
  getDruidState,
  (state) => state.custom_event_list || EMPTY_ARRAY
);

export const getCustomEventNameList: (state: RootState) => string[] = createSelector(getCustomEventList, (customEventKeys) =>
  customEventKeys.map(mapToEventName)
);

export const selectHasCustomerEventAliasesLast30d: (state: RootState) => boolean | null | undefined = createSelector(
  getDruidState,
  (state) => state.hasCustomerEventAliasesLast30d
);

export const getDruidLastRateLimitHit: (state: RootState) => number | undefined = createSelector(
  getDruidState,
  getFlipperState,
  (druid, flipper) => {
    if (flipper.features && flipper.features.druid_rate_limit_modal) {
      return druid.lastRateLimitHit;
    }

    return undefined;
  }
);

export const selectDruidLimit: (state: RootState) => number | null | undefined = createSelector(
  getDruidState,
  (state) => state.druidLimit
);
